import {Component, ElementRef, QueryList, ViewChildren} from '@angular/core';
import {WorkbenchCommandRegistry} from '../../../services/workbench-command-registry.service';
import {AuditActivityId, ProjectId, TestRunId, VideoRecordingId} from '@webmate-web-core/shared/types/ids';
import {InspectorImplementation, OpenVideoRecordingEvent} from '../../../types';
import {convertBytesToSuitableUnitsString} from '@webmate-web-core/shared/utils/utils';
import {WorkbenchContextService} from '../../../services/workbench-context.service';
import {WorkbenchTestService} from '../../../services/workbench-test.service';
import {AddToFindingEvent} from '../inspector-shared-tools/add-to-finding.component';
import {delay, switchMap, take} from 'rxjs/operators';
import moment from 'moment';
import {VideoRecordingService} from '@webmate-web-core/shared/services/video-recording.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {SearchService} from '@webmate-web-core/shared/services/search.service';

@UntilDestroy()
@Component({
  selector: 'app-video-recording-inspector',
  templateUrl: './video-recording-inspector.component.html',
  styleUrl: './video-recording-inspector.component.less'
})
export class VideoRecordingInspectorComponent implements InspectorImplementation {

    @ViewChildren('nameInput') nameInput: QueryList<ElementRef>;

    projectId: ProjectId;
    videoId: VideoRecordingId;
    name: string;
    videoUrl: string;
    duration?: number;
    size?: string;
    format?: string;
    addedToFinding: boolean = false;

    modifyingName: boolean = false;
    modifiedName: string;

    activeTestRun?: TestRunId;
    activeActivity?: AuditActivityId;

    constructor(private readonly workbenchCommandRegistry: WorkbenchCommandRegistry,
                private contextService: WorkbenchContextService,
                private workbenchTestService: WorkbenchTestService,
                private readonly videoRecordingService: VideoRecordingService,
                private searchService: SearchService) {
    }

    ngOnInit() {
        this.projectId = this.contextService.getProjectId();
        this.contextService.subscribeWorkbenchContext$().subscribe(context => {
            this.activeTestRun = context?.testRunContext?.activeTestRun;
            this.activeActivity = context?.auditContext?.activeAuditActivity?.id;
        });
    }

    ngAfterViewInit() {
        this.nameInput.changes.subscribe(changes => {
            // this is needed to set the focus automatically after the resultInput is rendered
            this.nameInput.first?.nativeElement.focus();
        });
    }

    handleInspectorEvent(event: OpenVideoRecordingEvent): void {
        this.videoId = event.id;
        this.videoUrl = event.videoUrl;
        this.duration = event.duration;
        this.size = event.size ? convertBytesToSuitableUnitsString(event.size) : undefined;
        this.format = event.format;

        if (this.videoId) {
            this.searchService.getIndexingNotifications(this.projectId).pipe(
                untilDestroyed(this),
                delay(200),
                switchMap(() => {
                    return this.videoRecordingService.getVideoRecording(this.videoId);
                })
            ).subscribe(video => {
                this.name = video.metadata.name;
            });
            this.videoRecordingService.getVideoRecording(this.videoId).subscribe(video => {
                this.name = video.metadata.name;
            })
        }
    }

    downloadVideo(){
        this.workbenchCommandRegistry.executeCommand("workbench-tools:downloadVideoRecording", {name: this.name, videoUrl: this.videoUrl});
    }

    addToFinding(event: AddToFindingEvent) {
        this.workbenchTestService.addVideoToFinding(event.findingId, this.videoId, event.optDescription).pipe(take(1)).subscribe(response =>{
            this.addedToFinding = true;
            setTimeout(()=> this.addedToFinding = false, 5000);
        })
    }

    changeName() {
        this.videoRecordingService.setVideoName(this.videoId, this.modifiedName).subscribe(() => {
            this.name = this.modifiedName;
            this.modifyingName = false;
        });
    }

    formatDuration(ms: number) {
        const duration = moment.duration(ms);
        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        const parts: string[] = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0) parts.push(`${seconds}s`);

        return parts.length > 0 ? parts.join(" ") : "0s";
    }

    protected readonly isFinite = isFinite;
}
